import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import './AssetSelect.css';
import { TokenLogo, ChainLogo } from './CryptoLogo';

/**
 * Custom dropdown that shows a real token/chain logo next to each option —
 * a drop-in replacement for a native <select> (which can't render images).
 *
 * Props:
 *   options:  [{ value, label, sublabel?, symbol?, chain?, logoUrl? }]
 *   value:    currently-selected value (compared as string)
 *   onChange: (value) => void   // NOTE: receives the raw value, not an event
 *   kind:     'token' | 'chain'  (which logo to render)
 *   placeholder, disabled
 */
const AssetSelect = ({
  options = [],
  value,
  onChange,
  kind = 'token',
  placeholder = 'Select',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selected = options.find((o) => String(o.value) === String(value));

  const renderLogo = (o, size) =>
    kind === 'chain' ? (
      <ChainLogo chain={o.chain ?? o.symbol ?? o.label} logoUrl={o.logoUrl} size={size} />
    ) : (
      <TokenLogo symbol={o.symbol ?? o.label} logoUrl={o.logoUrl} size={size} />
    );

  const logoSize = kind === 'chain' ? 22 : 24;

  return (
    <div className={`asset-select ${disabled ? 'is-disabled' : ''} ${open ? 'is-open' : ''} ${className}`} ref={ref}>
      <button
        type="button"
        className="asset-select__control"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className="asset-select__value">
            {renderLogo(selected, logoSize)}
            <span className="asset-select__labels">
              <span className="asset-select__label">{selected.label}</span>
              {selected.sublabel ? <span className="asset-select__sub">{selected.sublabel}</span> : null}
            </span>
          </span>
        ) : (
          <span className="asset-select__placeholder">{placeholder}</span>
        )}
        <ChevronDown size={16} className={`asset-select__chevron ${open ? 'is-open' : ''}`} />
      </button>

      {open && !disabled && (
        <ul className="asset-select__menu" role="listbox">
          {options.length === 0 ? (
            <li className="asset-select__empty">No options available</li>
          ) : (
            options.map((o) => {
              const active = String(o.value) === String(value);
              return (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={active}
                  className={`asset-select__opt ${active ? 'is-active' : ''}`}
                  onClick={() => {
                    onChange?.(o.value);
                    setOpen(false);
                  }}
                >
                  {renderLogo(o, logoSize)}
                  <span className="asset-select__labels">
                    <span className="asset-select__label">{o.label}</span>
                    {o.sublabel ? <span className="asset-select__sub">{o.sublabel}</span> : null}
                  </span>
                  {active ? <Check size={15} className="asset-select__check" /> : null}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default AssetSelect;
